import React, { useEffect, useState } from "react";
import { isHeicFile, convertHeicToJpeg } from "@/lib/utils/heic-converter";
import { useRecordWebcam, UseRecordWebcam } from "react-record-webcam";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Camera,
  Download,
  Loader2,
  Pause,
  Play,
  RefreshCcw,
  Send,
  StopCircle,
  Upload,
} from "lucide-react";
import { cn } from "@/lib/utils.ts";
import { useNotificationMuteActivity } from "@/lib/hooks/conversations/use-notification-mute-activity";
import { MutingVideo } from "@/components/shared/muting-media";

interface VideoRecordingDialogTriggerProps {
  handleSendFile: (file: File) => Promise<void>;
}

export const VideoRecordingDialogTrigger: React.FC<
  VideoRecordingDialogTriggerProps
> = (props) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button onClick={() => setIsOpen(true)}>
        <Camera className="text-muted-foreground" />
      </button>
      {isOpen && (
        <VideoRecorderDialog {...props} setIsOpen={setIsOpen} isOpen={isOpen} />
      )}
    </>
  );
};

interface VideoRecorderDialogProps extends VideoRecordingDialogTriggerProps {
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
}

const VideoRecorderDialog = ({
  handleSendFile,
  setIsOpen,
  isOpen,
}: VideoRecorderDialogProps) => {
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState<"initial" | "select" | "record" | "preview">(
    "initial",
  );

  const {
    activeRecordings,
    createRecording,
    openCamera,
    closeCamera,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    download,
    devicesByType,
    clearAllRecordings,
    applyRecordingOptions,
  } = useRecordWebcam({
    options: { fileType: "mp4" },
    mediaRecorderOptions: { mimeType: "video/mp4;codecs=avc1" },
  });

  const [currentRecordingId, setCurrentRecordingId] = useState<string | null>(
    null,
  );
  const [videoDeviceId, setVideoDeviceId] = useState<string>("");
  const [audioDeviceId, setAudioDeviceId] = useState<string>("");

  const isRecording = activeRecordings.some(
    (r) => r.status === "RECORDING" || r.status === "PAUSED",
  );
  useNotificationMuteActivity(isRecording, "video-recording");

  useEffect(() => {
    if (devicesByType?.video?.length) {
      setVideoDeviceId(devicesByType.video[0].deviceId);
    }
    if (devicesByType?.audio?.length) {
      setAudioDeviceId(devicesByType.audio[0].deviceId);
    }
  }, [devicesByType]);

  const handleRecordVideo = async () => {
    setMode("select");
  };

  const handleNext = async () => {
    setMode("record");
    if (activeRecordings.length) {
      await clearAllRecordings();
    }
    const recording = await createRecording(videoDeviceId, audioDeviceId);
    if (recording) {
      setCurrentRecordingId(recording.id);
      applyRecordingOptions(recording.id);
      await openCamera(recording.id);
    }
  };

  const handleStartRecording = async () => {
    if (currentRecordingId) {
      await startRecording(currentRecordingId);
    }
  };

  const handleStopRecording = async () => {
    if (currentRecordingId) {
      await stopRecording(currentRecordingId);
      await closeCamera(currentRecordingId);
      setMode("preview");
    }
  };

  const handlePauseResumeRecording = async () => {
    if (currentRecordingId) {
      const recording = activeRecordings.find(
        (r) => r.id === currentRecordingId,
      );
      if (recording?.status === "RECORDING") {
        await pauseRecording(currentRecordingId);
      } else if (recording?.status === "PAUSED") {
        await resumeRecording(currentRecordingId);
      }
    }
  };

  const handleSend = async () => {
    setSending(true);
    const recording = activeRecordings.find((r) => r.id === currentRecordingId);

    if (!recording || !recording.blob) {
      setSending(false);
      return;
    }

    const blob = recording.blob;
    const file = new File([blob], "video.mp4", { type: "video/mp4" });

    // Get file size
    const fileSize = file.size;

    if (fileSize > 256 * 1024 * 1024) {
      alert("File size is too large. Please upload a file less than 256MB.");
      setSending(false);
      return;
    }

    await handleSendFile(file);
    setSending(false);
    await handleClose();
  };

  const handleDownload = () => {
    if (currentRecordingId) {
      download(currentRecordingId);
    }
  };

  const handleRestart = async () => {
    if (currentRecordingId) {
      await closeCamera(currentRecordingId);
      setMode("initial");
      setCurrentRecordingId(null);
    }
  };

  const handleClose = async () => {
    if (activeRecordings.length) {
      await clearAllRecordings();
    }
    setMode("initial");
    setCurrentRecordingId(null);
    setIsOpen(false);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(newIsOpen) => {
        if (!newIsOpen) {
          handleClose();
        }

        setIsOpen(newIsOpen);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === "initial"
              ? "Select an Option"
              : mode === "select"
                ? "Select Devices"
                : "Video Recorder"}
          </DialogTitle>
          <DialogDescription>
            {mode === "initial"
              ? "Record a video or select a file to upload."
              : mode === "select"
                ? "Select video and audio devices."
                : "Record and preview your video."}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4">
          {mode === "initial" && (
            <div className="flex flex-col justify-center gap-2">
              <Button onClick={handleRecordVideo}>
                <Camera className="mr-2 h-4 w-4" /> Record Video
              </Button>

              <div className="flex items-center justify-center gap-4">
                <hr className="w-full border-gray-300" />
                <span className="text-gray-500">OR</span>
                <hr className="w-full border-gray-300" />
              </div>

              <input
                type="file"
                id="file-input"
                className="hidden"
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    let fileToSend = file;
                    if (isHeicFile(file)) {
                      try {
                        fileToSend = await convertHeicToJpeg(file);
                      } catch (error) {
                        console.error("Failed to convert HEIC:", error);
                      }
                    }
                    handleSendFile(fileToSend);
                    handleClose();
                  }
                }}
                accept="video/*, image/*, audio/*, .heic, .heif"
              />
              <Button
                className="w-full"
                variant="secondary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  document?.getElementById?.("file-input")?.click();
                }}
              >
                <Upload className="mr-2 h-4 w-4" /> Select File
              </Button>
            </div>
          )}
          {mode === "select" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="video-device">Video Device</label>
                <Select value={videoDeviceId} onValueChange={setVideoDeviceId}>
                  <SelectTrigger id="video-device">
                    <SelectValue placeholder="Select video device" />
                  </SelectTrigger>
                  <SelectContent>
                    {devicesByType?.video?.map((device) => (
                      <SelectItem key={device.deviceId} value={device.deviceId}>
                        {device.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label htmlFor="audio-device">Audio Device</label>
                <Select value={audioDeviceId} onValueChange={setAudioDeviceId}>
                  <SelectTrigger id="audio-device">
                    <SelectValue placeholder="Select audio device" />
                  </SelectTrigger>
                  <SelectContent>
                    {devicesByType?.audio?.map((device) => (
                      <SelectItem key={device.deviceId} value={device.deviceId}>
                        {device.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-center">
                <Button onClick={handleNext}>Next</Button>
              </div>
            </div>
          )}
          {mode === "record" && currentRecordingId && (
            <RecordingMode
              recordingId={currentRecordingId}
              activeRecordings={activeRecordings}
              onStart={handleStartRecording}
              onStop={handleStopRecording}
              onPauseResume={handlePauseResumeRecording}
            />
          )}
          {(mode === "preview" || mode === "record") && currentRecordingId && (
            <PreviewMode
              recordingId={currentRecordingId}
              activeRecordings={activeRecordings}
              onSend={handleSend}
              onDownload={handleDownload}
              onRestart={handleRestart}
              sending={sending}
              mode={mode}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface RecordingModeProps {
  recordingId: string;
  activeRecordings: UseRecordWebcam["activeRecordings"];
  onStart: () => void;
  onStop: () => void;
  onPauseResume: () => void;
}

const RecordingMode: React.FC<RecordingModeProps> = ({
  recordingId,
  activeRecordings,
  onStart,
  onStop,
  onPauseResume,
}) => {
  const recording = activeRecordings.find((r) => r.id === recordingId);
  const [duration, setDuration] = useState(0);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (recording?.status === "RECORDING") {
      const id = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
      setIntervalId(id);
    } else if (recording?.status === "PAUSED" && intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    } else if (recording?.status === "STOPPED" && intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [recording?.status]);

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  if (!recording) return null;

  return (
    <div className="space-y-4">
      <div className="relative">
        <video
          ref={recording.webcamRef}
          autoPlay
          playsInline
          muted
          className="w-full"
        />
        <span className="absolute right-0 top-0 rounded-bl-lg bg-gray-200 px-2 py-1 text-sm text-gray-700">
          {formatDuration(duration)}
        </span>
      </div>

      <div className="flex items-center justify-center space-x-2">
        {recording.status !== "RECORDING" && recording.status !== "PAUSED" && (
          <Button onClick={onStart}>
            <Camera className="mr-2 h-4 w-4" />
            Start
          </Button>
        )}
        {(recording.status === "RECORDING" ||
          recording.status === "PAUSED") && (
          <Button
            onClick={onStop}
            disabled={
              recording.status !== "RECORDING" && recording.status !== "PAUSED"
            }
          >
            <StopCircle className="mr-2 h-4 w-4" />
            Finish
          </Button>
        )}
        {(recording.status === "RECORDING" ||
          recording.status === "PAUSED") && (
          <Button
            onClick={onPauseResume}
            disabled={
              recording.status !== "RECORDING" && recording.status !== "PAUSED"
            }
            variant="secondary"
          >
            {recording.status === "RECORDING" ? (
              <Pause className="mr-2 h-4 w-4" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            {recording.status === "PAUSED" ? "Resume" : "Pause"}
          </Button>
        )}
      </div>
    </div>
  );
};

interface PreviewModeProps {
  recordingId: string;
  activeRecordings: UseRecordWebcam["activeRecordings"];
  onSend: () => void;
  onDownload: () => void;
  onRestart: () => void;
  sending?: boolean;
  mode: "preview" | "record";
}

const PreviewMode: React.FC<PreviewModeProps> = ({
  recordingId,
  activeRecordings,
  onSend,
  onDownload,
  onRestart,
  sending,
  mode,
}) => {
  const recording = activeRecordings.find((r) => r.id === recordingId);

  return (
    <div
      className={cn("space-y-4", {
        hidden: mode === "record",
      })}
    >
      <MutingVideo
        key={recording?.id}
        ref={recording?.previewRef}
        loop
        playsInline
        controls
      />

      <div className="flex justify-center space-x-2">
        <Button onClick={onSend} disabled={sending}>
          {sending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Send className="mr-2 h-4 w-4" />
          )}{" "}
          Send
        </Button>
        <Button onClick={onDownload} variant="secondary">
          <Download className="mr-2 h-4 w-4" /> Download
        </Button>
        <Button onClick={onRestart} variant="outline">
          <RefreshCcw className="mr-2 h-4 w-4" /> Record again
        </Button>
      </div>
    </div>
  );
};
